/*
  QUIVER - Build Your QUIVER progress.

  Adding to cart is NOT handled here: each card uses the theme's own
  <product-form>, which posts to the cart, opens the cart notification or
  drawer and publishes the theme's "cart-update" event.

  This file only:
  1. counts the prints in the real cart (/cart.js) and updates the progress
     bar and message, on load and after every cart change;
  2. flashes the "added" label on the button that was just used.

  A cart line counts when it is not a gift card and it is a print, meaning
  it has the size or frame option. If the section is set to "unframed
  prints only", its frame option must also equal the unframed value.
*/
if (!customElements.get('quiver-byq')) {
  customElements.define(
    'quiver-byq',
    class QuiverBuildYourQuiver extends HTMLElement {
      connectedCallback() {
        this.threshold = parseInt(this.dataset.threshold, 10) || 3;
        this.percent = this.dataset.percent || '';
        this.countAll = this.dataset.countMode !== 'unframed';
        this.sizeOption = this.dataset.sizeOption || 'Print size';
        this.frameOption = this.dataset.frameOption || 'Frame';
        this.unframedValue = this.dataset.unframedValue || 'No frame';
        this.messageEl = this.querySelector('[data-byq-message]');
        this.barEl = this.querySelector('[data-byq-bar]');
        this.steps = Array.from(this.querySelectorAll('[data-byq-step]'));
        this.pendingForm = null;
        this.requestNumber = 0;

        this.trimCards();

        this.onSubmit = (event) => {
          const form = event.target.closest('[data-byq-form]');
          if (form) this.pendingForm = form;
        };
        this.addEventListener('submit', this.onSubmit, true);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            this.flashAdded(event);
            this.refresh();
          });
          this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, () => {
            this.pendingForm = null;
          });
        }

        // Back/forward cache can restore the page with an out-of-date count.
        this.onPageShow = (event) => {
          if (event.persisted) this.refresh();
        };
        window.addEventListener('pageshow', this.onPageShow);

        this.refresh();
      }

      disconnectedCallback() {
        this.removeEventListener('submit', this.onSubmit, true);
        window.removeEventListener('pageshow', this.onPageShow);
        if (this.unsubscribeUpdate) this.unsubscribeUpdate();
        if (this.unsubscribeError) this.unsubscribeError();
      }

      // Safety net for the collection top-up: never show the product being
      // viewed, and never show more cards than the section asks for.
      trimCards() {
        const limit = parseInt(this.dataset.limit, 10);
        const host = this.closest('product-recommendations');
        const currentId = host ? host.dataset.productId : null;
        const cards = Array.from(this.querySelectorAll('.byq__item'));
        if (currentId) {
          cards.filter((card) => card.dataset.productId === currentId).forEach((card) => card.remove());
        }
        if (!limit) return;
        Array.from(this.querySelectorAll('.byq__item'))
          .slice(limit)
          .forEach((card) => card.remove());
      }

      countPrints(cart) {
        if (!cart || !Array.isArray(cart.items)) return null;
        return cart.items.reduce((total, item) => {
          if (item.gift_card) return total;
          const options = Array.isArray(item.options_with_values) ? item.options_with_values : [];
          const counts = this.countAll
            ? options.some((option) => option.name === this.frameOption || option.name === this.sizeOption)
            : options.some((option) => option.name === this.frameOption && option.value === this.unframedValue);
          return counts ? total + (parseInt(item.quantity, 10) || 0) : total;
        }, 0);
      }

      refresh() {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        const requestNumber = ++this.requestNumber;

        return fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
          .then((response) => response.json())
          .then((cart) => {
            // Ignore a slow response that has been overtaken by a newer one.
            if (requestNumber !== this.requestNumber) return;
            const count = this.countPrints(cart);
            if (count !== null) this.render(count);
          })
          .catch((error) => console.error(error));
      }

      render(count) {
        const filled = Math.min(count, this.threshold);
        const remaining = Math.max(this.threshold - count, 0);

        let template = this.dataset.messageStart;
        if (count >= this.threshold) template = this.dataset.messageDone;
        else if (count > 0) template = this.dataset.messageProgress;

        const message = (template || '')
          .split('[percent]').join(this.percent)
          .split('[threshold]').join(this.threshold)
          .split('[count]').join(count)
          .split('[remaining]').join(remaining);

        if (this.messageEl && this.messageEl.textContent !== message) this.messageEl.textContent = message;
        this.steps.forEach((step, index) => step.classList.toggle('is-filled', index < filled));
        if (this.barEl) {
          this.barEl.setAttribute('aria-valuenow', filled);
          this.barEl.setAttribute('aria-valuetext', message);
        }
        this.classList.toggle('byq--unlocked', count >= this.threshold);
      }

      flashAdded(event) {
        const form = this.pendingForm;
        this.pendingForm = null;
        if (!form || !event || event.source !== 'product-form') return;

        const select = form.querySelector('[name="id"]');
        if (!select || String(select.value) !== String(event.productVariantId)) return;

        const button = form.querySelector('[type="submit"]');
        const label = form.querySelector('[data-byq-label]');
        const addedLabel = this.dataset.addedLabel;
        if (!button || !label || !addedLabel) return;

        if (!label.dataset.original) label.dataset.original = label.textContent;
        label.textContent = addedLabel;
        button.classList.add('is-added');

        clearTimeout(form.byqTimer);
        form.byqTimer = setTimeout(() => {
          label.textContent = label.dataset.original;
          button.classList.remove('is-added');
        }, 2200);
      }
    }
  );
}
